/* eslint-disable */
import React, { useState } from 'react';
import {
  IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Divider,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ReactComponent as KeyIcon } from 'Images/key_icon.svg';
import { ReactComponent as ResetMfaIcon } from 'Images/reset_mfa_icon.svg';
import { ReactComponent as LockIcon } from 'Images/lock_icon.svg';
import { ReactComponent as DeleteIcon } from 'Images/delete_icon.svg';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import toast from 'Utils/toast';
import ConfirmDialog from 'Shared/ConfirmDialog';
import { resetUserMfa, disableUserMfa, enableUserMfa, deleteUser, unlockUser } from '../UsersService';
import ResetPasswordDialog from './ResetPasswordDialog';

const notifyRefresh = () => window.dispatchEvent(new Event('subusers-refresh'));

const SubUserActionsMenu = ({ row }) => {
  const [anchor, setAnchor] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [resetPwdOpen, setResetPwdOpen] = useState(false);

  const openConfirm = (title, content, highlight, onConfirm, contentSuffix) => {
    setAnchor(null);
    setConfirm({ title, content, highlight, onConfirm, contentSuffix });
  };

  const handleResetMfa = async () => {
    try {
      if (row.mfa === 'disabled') {
        await enableUserMfa(row.id);
      } else {
        await resetUserMfa(row.id);
      }
      toast.success('MFA réinitialisée avec succès');
      notifyRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.statusLabel || 'Erreur lors de la réinitialisation de la MFA');
    }
  };

  const handleDisableMfa = async () => {
    try {
      await disableUserMfa(row.id);
      toast.success('MFA désactivée avec succès');
      notifyRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.statusLabel || 'Erreur lors de la désactivation de la MFA');
    }
  };

  const handleUnlock = async () => {
    try {
      await unlockUser(row.id);
      toast.success('Compte déverrouillé avec succès');
      notifyRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.statusLabel || 'Erreur lors du déverrouillage du compte');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteUser(row.id);
      toast.success('Utilisateur supprimé avec succès');
      notifyRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.statusLabel || 'Erreur lors de la suppression de l\'utilisateur');
    }
  };

  const isLockedOut = !row.enabled && row.errors >= 5;

  return (
    <>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}>
        <MoreVertIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: '8px', minWidth: 280 } } }}
      >
        <MenuItem
          onClick={() => openConfirm(
            'Réinitialiser le mot de passe ?',
            'Un nouveau mot de passe sera généré pour',
            row.username || 'cet utilisateur',
            () => setResetPwdOpen(true),
          )}
          sx={{ py: '7px', px: '12px', gap: '2px' }}
        >
          <ListItemIcon sx={{ minWidth: 'unset', ml: '6px', mr: '-12px' }}>
            <KeyIcon style={{ width: 18, height: 18 }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px', color: '#385576', fontWeight: 600 }}>
            Réinitialiser le mot de passe
          </ListItemText>
        </MenuItem>
        {isLockedOut && (
          <>
            <Divider sx={{ my: 0, mx: '14px', borderColor: '#F1F5F9' }} />
            <MenuItem
              onClick={() => openConfirm(
                'Déverrouiller le compte ?',
                'Le compte de',
                row.username || 'cet utilisateur',
                handleUnlock,
                'sera déverrouillé et réactivé.',
              )}
              sx={{ py: '7px', px: '12px', gap: '2px' }}
            >
              <ListItemIcon sx={{ minWidth: 'unset', ml: '6px', mr: '-12px' }}>
                <LockOpenIcon sx={{ fontSize: 18, color: '#0AD69F' }} />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '14px', color: '#0AD69F', fontWeight: 600 }}>
                Déverrouiller le compte
              </ListItemText>
            </MenuItem>
          </>
        )}
        <Divider sx={{ my: 0, mx: '14px', borderColor: '#F1F5F9' }} />
        <MenuItem
          onClick={() => openConfirm(
            row.mfa === 'disabled' ? 'Activer la MFA ?' : 'Réinitialiser la MFA ?',
            row.mfa === 'disabled' ? "L'authentification à deux facteurs sera activée pour" : 'La configuration MFA de',
            row.username || 'cet utilisateur',
            handleResetMfa,
            row.mfa === 'disabled' ? undefined : 'sera réinitialisée',
          )}
          sx={{ py: '7px', px: '12px', gap: '2px' }}
        >
          <ListItemIcon sx={{ minWidth: 'unset', ml: '6px', mr: '-12px' }}>
            {row.mfa === 'disabled'
              ? <LockOpenIcon sx={{ fontSize: 18, color: '#385576' }} />
              : <ResetMfaIcon style={{ width: 18, height: 18 }} />}
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px', color: '#385576', fontWeight: 600 }}>
            {row.mfa === 'disabled' ? "Activer l'authentification à deux facteurs (MFA)" : "Réinitialiser l'authentification à deux facteurs (MFA)"}
          </ListItemText>
        </MenuItem>
        {row.mfa !== 'disabled' && (
          <>
            <Divider sx={{ my: 0, mx: '14px', borderColor: '#F1F5F9' }} />
            <MenuItem
              onClick={() => openConfirm(
                'Désactiver la MFA ?',
                'La configuration MFA sera désactivée pour',
                row.username || 'cet utilisateur',
                handleDisableMfa,
              )}
              sx={{ py: '7px', px: '12px', gap: '2px' }}
            >
              <ListItemIcon sx={{ minWidth: 'unset', ml: '6px', mr: '-12px' }}>
                <LockIcon style={{ width: 18, height: 18 }} />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '14px', color: '#385576', fontWeight: 600 }}>
                Désactiver l'authentification à deux facteurs (MFA)
              </ListItemText>
            </MenuItem>
          </>
        )}
        <Divider sx={{ my: 0, mx: '14px', borderColor: '#F1F5F9' }} />
        <MenuItem
          onClick={() => openConfirm(
            'Confirmer la suppression',
            'Le compte utilisateur sera définitivement supprimé, Cette action est irréversible.',
            null,
            handleDelete,
          )}
          sx={{ py: '7px', px: '12px', gap: '2px' }}
        >
          <ListItemIcon sx={{ minWidth: 'unset', ml: '6px', mr: '-12px' }}>
            <DeleteIcon style={{ width: 18, height: 18 }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px', color: '#EF456E', fontWeight: 600 }}>
            Supprimer l'utilisateur
          </ListItemText>
        </MenuItem>
      </Menu>

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          content={confirm.content}
          highlight={confirm.highlight}
          contentSuffix={confirm.contentSuffix}
          handleClose={() => setConfirm(null)}
          handleClick={() => confirm.onConfirm && confirm.onConfirm()}
        />
      )}

      {resetPwdOpen && (
        <ResetPasswordDialog
          user={row}
          onClose={() => setResetPwdOpen(false)}
        />
      )}
    </>
  );
};

export default SubUserActionsMenu;
